import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'

import {
  FACES_DEFAULTS,
  hiddenAtStart,
  lifterUnsure,
  peopleToOffer,
  type FaceMap,
  type FacesDefault,
} from '@gymcloud/faces'

import { useAuth } from '~/auth/auth-context'
import { Dialog } from '~/components/dialog'
import { CloseIcon, FaceIcon } from '~/components/icons'
import { FilterChip } from '~/components/ui/filter-chip'
import { WorkingButton } from '~/components/working'
import { useApi } from '~/data/data-context'
import type { LoggedSet } from '~/data/types'
import { canHideFacesHere } from '~/lib/face-maps'
import type { CopyState, FacesCopies } from '~/lib/faces-copies'
import {
  HandedCopies,
  mapOfSet,
  START_LABELS,
  type Asking,
  type FacesShowing,
} from '~/lib/faces-plan'
import { cn } from '~/lib/cn'
import { formatSetNumbers } from '~/lib/units'

/*
 * The faces step — `/specs/features/faces.spec.md`, Leaving the app. A download can blur faces,
 * and this is where it is settled whose: the copy is made as people are pressed, and the set
 * viewer shows it in the video's place. The web never finds faces: it reads the map a phone
 * found and sent up, and a video with none goes only with nobody's blurred.
 */

/**
 * Each person's face as the step shows it, cut from the video in the browser at their largest
 * sighting. Nothing is sent anywhere: the pictures are made here, from the video already here.
 */
function useFaceCrops(videoUrl: string, map: FaceMap | null): Map<string, string> {
  const [crops, setCrops] = useState<Map<string, string>>(new Map())
  useEffect(() => {
    if (!map || typeof document === 'undefined') {
      return
    }
    let live = true
    const offered = peopleToOffer(map)
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.preload = 'auto'
    video.src = videoUrl
    const made = new Map<string, string>()
    const at = (seconds: number) =>
      new Promise<void>((resolve) => {
        const done = () => {
          video.removeEventListener('seeked', done)
          resolve()
        }
        video.addEventListener('seeked', done)
        video.currentTime = seconds
      })
    const work = async () => {
      await new Promise<void>((resolve) => {
        video.addEventListener(
          'loadeddata',
          () => {
            resolve()
          },
          { once: true },
        )
      })
      for (const { person, best } of offered) {
        if (!live) return
        await at(best[0] / 1000)
        const width = video.videoWidth
        const height = video.videoHeight
        const side = Math.max(best[3] * width, best[4] * height) * 1.6
        const canvas = document.createElement('canvas')
        canvas.width = 160
        canvas.height = 160
        canvas
          .getContext('2d')
          ?.drawImage(
            video,
            best[1] * width - side / 2,
            best[2] * height - side / 2,
            side,
            side,
            0,
            0,
            160,
            160,
          )
        made.set(person.id, canvas.toDataURL('image/jpeg', 0.8))
        setCrops(new Map(made))
      }
    }
    void work().catch(() => undefined)
    return () => {
      live = false
      video.removeAttribute('src')
      video.load()
    }
  }, [videoUrl, map])
  return crops
}

/** A copy asked for and not yet begun, which reads as one just begun. */
const JUST_BEGUN: CopyState = { kind: 'making', progress: 0, standing: null }

/** Where a set's face map stands in the dialog: being read, read, or there being none. */
type MapState =
  | { readonly read: false }
  | { readonly read: true; readonly map: FaceMap | null; readonly unreached?: boolean }

export function FacesStepDialog({
  asking,
  mapUrl,
  docked,
  copies,
  returnFocus,
  onShowing,
}: {
  asking: Asking
  /** Where the focus was as the step was asked for — read before anything under it went inert. */
  returnFocus: { readonly current: Element | null }
  mapUrl: (setId: string) => string
  /** Stood as a column beside the page rather than over it, leaving the page usable. */
  docked: boolean
  /** The copies made for the page this stands on, kept across openings. */
  copies: FacesCopies
  /** Where the copy of the set shown stands — the first, until a person in another is pressed. */
  onShowing: (showing: FacesShowing | null) => void
}) {
  const api = useApi()
  const { units, distanceUnits } = useAuth()
  const able = canHideFacesHere()
  const [maps, setMaps] = useState<ReadonlyMap<string, MapState>>(new Map())
  const [hidden, setHidden] = useState<ReadonlyMap<string, ReadonlySet<string>>>(new Map())
  // The starting point last pressed — the account's, until one is.
  const [pressed, setPressed] = useState<FacesDefault>(asking.start)
  const [working, setWorking] = useState(false)
  const [shownSet, setShownSet] = useState(asking.sets[0]?.id ?? '')
  // The start each map takes as it arrives, read where it lands rather than where it was asked.
  const startNow = useRef(pressed)
  useLayoutEffect(() => {
    startNow.current = pressed
  }, [pressed])

  // Each set's map, fetched again on a try after it could not be reached.
  const [tries, setTries] = useState<ReadonlyMap<string, number>>(new Map())
  const triesKey = asking.sets.map((set) => `${set.id}:${String(tries.get(set.id) ?? 0)}`).join(';')
  useEffect(() => {
    let live = true
    for (const set of asking.sets) {
      mapOfSet(set, mapUrl).then(
        (map) => {
          if (!live) return
          setMaps((held) => new Map(held).set(set.id, { read: true, map }))
          if (map) {
            setHidden((held) =>
              held.has(set.id)
                ? held
                : new Map(held).set(set.id, hiddenAtStart(map, startNow.current)),
            )
          }
        },
        () => {
          if (live)
            setMaps((held) => new Map(held).set(set.id, { read: true, map: null, unreached: true }))
        },
      )
    }
    return () => {
      live = false
    }
    // The key says which tries have been asked for.
  }, [asking, mapUrl, triesKey])

  const found = new Map<string, FaceMap>()
  for (const [setId, state] of maps) {
    if (state.read && state.map) {
      found.set(setId, state.map)
    }
  }
  /*
   * The starting point the faces as they stand match, which is the chip marked: the one last
   * pressed while it still matches, and otherwise the first that does — or none, where the
   * faces are a choice no starting point makes.
   */
  const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>) =>
    a.size === b.size && [...a].every((each) => b.has(each))
  const matches = (each: FacesDefault) =>
    [...found].every(([setId, map]) =>
      sameSet(hiddenAtStart(map, each), hidden.get(setId) ?? new Set<string>()),
    )
  const start: FacesDefault | null =
    found.size === 0 || matches(pressed) ? pressed : (FACES_DEFAULTS.find(matches) ?? null)
  const ready = start === 'none' || found.size === asking.sets.length

  // Whose faces each copy blurs, as the dialog stands.
  const blurredIn = (setId: string): ReadonlySet<string> => hidden.get(setId) ?? new Set<string>()

  // Every change begins the copies it asks for at once, giving up any under way for another.
  const wanted = asking.sets
    .map((set) => [set, found.get(set.id), blurredIn(set.id)] as const)
    .filter(
      (each): each is readonly [LoggedSet, FaceMap, ReadonlySet<string>] => each[1] !== undefined,
    )
  const wantedKey = wanted
    .map(([set, , blurred]) => `${set.id}:${[...blurred].sort().join(',')}`)
    .join(';')
  useEffect(() => {
    for (const [set, map, blurred] of wanted) {
      copies.want(set, map, blurred)
    }
    // The key says everything the copies depend on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copies, wantedKey])

  const states = useSyncExternalStore(
    (listener) => copies.subscribe(listener),
    () => copies.current,
  )
  const shownCopy = found.has(shownSet) ? (states.get(shownSet) ?? JUST_BEGUN) : null
  useEffect(() => {
    onShowing(shownCopy ? { setId: shownSet, copy: shownCopy } : null)
  }, [onShowing, shownSet, shownCopy])
  useEffect(
    () => () => {
      onShowing(null)
    },
    [onShowing],
  )

  const choose = (each: FacesDefault) => {
    setPressed(each)
    setHidden(new Map([...found].map(([setId, map]) => [setId, hiddenAtStart(map, each)])))
  }

  const toggle = (setId: string, personId: string) => {
    const now = hidden.get(setId) ?? new Set<string>()
    const next = new Set(now)
    if (next.has(personId)) next.delete(personId)
    else next.add(personId)
    setHidden(new Map(hidden).set(setId, next))
    setShownSet(setId)
  }

  const go = async () => {
    setWorking(true)
    try {
      // Whatever is still being made is waited for, the press saying so.
      const made = new Map<string, Blob | null>()
      for (const set of asking.sets) {
        made.set(set.id, await copies.handOver(set, found.get(set.id) ?? null, blurredIn(set.id)))
      }
      const went = await asking.work(new HandedCopies(made))
      if (went) {
        asking.done(true)
      }
    } finally {
      setWorking(false)
    }
  }

  // Put away by its close, or by Escape — never by a press beside it.
  const close = () => {
    if (!working) asking.done(false)
  }

  const content = (
    <>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-medium">{asking.verb}</h2>
        <button
          type="button"
          aria-label="Close"
          disabled={working}
          className="flex h-10 w-10 items-center justify-center rounded-full text-ink-muted transition hover:bg-hover disabled:opacity-40"
          onClick={close}
        >
          <CloseIcon className="h-6 w-6" />
        </button>
      </div>
      <div role="group" aria-label="Whose faces are blurred" className="flex flex-wrap gap-2">
        {FACES_DEFAULTS.map((each) => (
          <FilterChip
            key={each}
            label={START_LABELS[each]}
            on={start === each}
            disabled={working || (!able && each !== 'none')}
            className="disabled:opacity-40"
            onToggle={() => {
              choose(each)
            }}
          />
        ))}
      </div>
      {!able && (
        <p className="text-sm text-ink-muted">
          This browser cannot blur faces in a copy, so videos are downloaded here with nobody’s
          blurred.
        </p>
      )}
      <div className="flex flex-col gap-4">
        {asking.sets.map((set) => (
          <SetFaces
            key={set.id}
            label={
              asking.sets.length > 1
                ? `${set.exerciseName} · ${formatSetNumbers(set, units, distanceUnits)}`
                : null
            }
            state={maps.get(set.id) ?? { read: false }}
            hidden={hidden.get(set.id) ?? new Set()}
            videoUrl={api.mediaUrl(set.id, 'video')}
            enabled={!working && able}
            lifterAsked={start === 'others'}
            copyFailed={states.get(set.id)?.kind === 'failed'}
            verb={asking.verb}
            onRetry={() => {
              setMaps((held) => new Map(held).set(set.id, { read: false }))
              setTries((held) => new Map(held).set(set.id, (held.get(set.id) ?? 0) + 1))
            }}
            onToggle={(personId) => {
              toggle(set.id, personId)
            }}
          />
        ))}
      </div>
      <WorkingButton
        onPress={go}
        disabled={!ready}
        className="self-end rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink transition disabled:opacity-40"
      >
        {asking.verb}
      </WorkingButton>
    </>
  )

  if (docked) {
    return (
      <DockedPanel label={asking.verb} onEscape={close} returnFocus={returnFocus}>
        {content}
      </DockedPanel>
    )
  }
  return (
    <Dialog label={asking.verb} onDismiss={close} dismissOnBackdrop={false} className="max-w-md">
      {content}
    </Dialog>
  )
}

/**
 * The step as a column along the right of the page, beside what it is about rather than over
 * it: nothing under it is shut out, and Escape puts it away as it would a dialog.
 */
function DockedPanel({
  label,
  onEscape,
  returnFocus,
  children,
}: {
  label: string
  onEscape: () => void
  returnFocus: { readonly current: Element | null }
  children: ReactNode
}) {
  const escape = useRef(onEscape)
  useLayoutEffect(() => {
    escape.current = onEscape
  }, [onEscape])
  const panel = useRef<HTMLElement>(null)
  // Focus comes into the panel as it opens, and goes back where it was as it closes.
  useEffect(() => {
    const before = returnFocus.current
    panel.current?.focus()
    return () => {
      if (before instanceof HTMLElement && before.isConnected) before.focus()
    }
  }, [returnFocus])
  useEffect(() => {
    // Taken first, and kept: Escape here puts the panel away and nothing under it — not the
    // page the panel stands on, which answers Escape too. A dialog opened over the panel keeps
    // its own Escape.
    const onKey = (event: KeyboardEvent) => {
      if (document.querySelector('[role="dialog"][aria-modal="true"]') !== null) return
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault()
        event.stopImmediatePropagation()
        escape.current()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
    }
  }, [])
  return (
    <aside
      ref={panel}
      role="dialog"
      aria-modal="false"
      aria-label={label}
      tabIndex={-1}
      // Along the foot on a narrow window, the video drawn back into the room above it so it
      // stays to be seen and used — see the viewer's `--foot`.
      className="absolute inset-x-0 bottom-0 z-20 flex h-[55%] flex-col gap-4 overflow-y-auto rounded-t-3xl bg-surface p-5 text-ink outline-none sm:inset-y-0 sm:right-0 sm:left-auto sm:h-auto sm:w-80 sm:rounded-none"
    >
      {children}
    </aside>
  )
}

/** One video's faces: being read, not found, or each person to show or blur. */
function SetFaces({
  label,
  state,
  hidden,
  videoUrl,
  enabled,
  lifterAsked,
  copyFailed,
  verb,
  onRetry,
  onToggle,
}: {
  label: string | null
  state: MapState
  hidden: ReadonlySet<string>
  videoUrl: string
  enabled: boolean
  lifterAsked: boolean
  copyFailed: boolean
  verb: string
  onRetry: () => void
  onToggle: (personId: string) => void
}) {
  const map = state.read ? state.map : null
  const crops = useFaceCrops(videoUrl, map)
  const offered = map ? peopleToOffer(map) : []
  return (
    <section className="flex flex-col gap-2">
      {label !== null && <p className="text-sm font-medium text-ink-muted">{label}</p>}
      {!state.read ? (
        <p className="text-sm text-ink-muted">Reading where the faces are…</p>
      ) : state.unreached === true ? (
        <>
          <p className="text-sm text-ink-muted">
            This video’s faces could not be fetched, so for now it goes only with nobody’s blurred.
          </p>
          <button
            type="button"
            className="self-start rounded-full px-3 py-1.5 text-sm text-accent transition hover:bg-hover"
            onClick={onRetry}
          >
            Try again
          </button>
        </>
      ) : map === null ? (
        <p className="text-sm text-ink-muted">
          This video’s faces have not been found on a phone, so it goes only with nobody’s blurred.
        </p>
      ) : offered.length === 0 ? (
        <p className="text-sm text-ink-muted">No faces were found in this video.</p>
      ) : (
        <>
          {lifterAsked && lifterUnsure(map) && (
            <p className="text-sm text-ink-muted">
              It is not clear which of these is you, so every face starts blurred. Press yours to
              show it.
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            {offered.map(({ person }, index) => {
              const blurred = hidden.has(person.id)
              const crop = crops.get(person.id)
              return (
                <button
                  key={person.id}
                  type="button"
                  role="checkbox"
                  aria-checked={!blurred}
                  aria-label={`Person ${String(index + 1)}`}
                  disabled={!enabled}
                  className="flex flex-col items-center gap-1"
                  onClick={() => {
                    onToggle(person.id)
                  }}
                >
                  <span
                    className={cn(
                      'flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-surface-bright transition-shadow',
                      blurred ? 'ring-1 ring-surface-bright' : 'ring-3 ring-accent',
                    )}
                  >
                    {/* Blurred as the copy will have it, and eased between the two. */}
                    <span
                      data-testid="face-picture"
                      data-blurred={blurred ? '' : undefined}
                      className="flex h-full w-full scale-110 items-center justify-center transition-[filter] duration-200 ease-out motion-reduce:transition-none"
                      style={{ filter: blurred ? 'blur(5px)' : 'blur(0px)' }}
                    >
                      {crop ? (
                        <img src={crop} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <FaceIcon className="h-7 w-7" />
                      )}
                    </span>
                  </span>
                  <span className="text-xs text-ink-muted">{blurred ? 'Blurred' : 'Shown'}</span>
                  {/* The lifter, as the phone took them — never where it could not tell. */}
                  {person.suggested && <span className="text-xs text-accent">You</span>}
                </button>
              )
            })}
          </div>
          {copyFailed && (
            <p className="text-sm text-ink-muted">
              Its copy could not be made. Press {verb} to try again.
            </p>
          )}
        </>
      )}
    </section>
  )
}
